import { apiFetch } from './apiClient.js';

export type CampaignStatus =
  | 'draft'
  | 'scheduled'
  | 'running'
  | 'completed'
  | 'archived';

export interface OutboundCampaign {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  connectorId: string;
  templateId: string;
  status: CampaignStatus;
  scheduledAt?: string;
  csvObjectKey?: string;
  contactsCount: number;
  deliveredCount: number;
  failedCount: number;
  variableMap?: Record<string, string>;
  rateLimitPerSecond?: number;
  startedAt?: string;
  completedAt?: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface OutboundCampaignCreate {
  name: string;
  description?: string;
  connectorId: string;
  templateId: string;
  scheduledAt?: string;
  rateLimitPerSecond?: number;
  variableMap?: Record<string, string>;
}

export type OutboundCampaignPatch = Partial<
  Pick<
    OutboundCampaign,
    | 'name'
    | 'description'
    | 'scheduledAt'
    | 'rateLimitPerSecond'
    | 'variableMap'
    | 'csvObjectKey'
    | 'contactsCount'
  >
>;

export interface UploadUrlResponse {
  uploadUrl: string;
  objectKey: string;
  expiresIn: number;
  requiredHeaders: Record<string, string>;
}

interface ListCampaignsResponse {
  items: OutboundCampaign[];
}

export async function listCampaigns(
  tenantId: string,
  opts: { includeArchived?: boolean; status?: CampaignStatus } = {},
): Promise<OutboundCampaign[]> {
  const params: string[] = [];
  if (opts.includeArchived) params.push('includeArchived=true');
  if (opts.status) params.push(`status=${encodeURIComponent(opts.status)}`);
  const qs = params.length ? `?${params.join('&')}` : '';
  const res = await apiFetch<ListCampaignsResponse | OutboundCampaign[]>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/campaigns${qs}`,
  );
  return Array.isArray(res) ? res : res.items;
}

export async function getCampaign(
  tenantId: string,
  campaignId: string,
): Promise<OutboundCampaign> {
  return apiFetch<OutboundCampaign>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/campaigns/${encodeURIComponent(campaignId)}`,
  );
}

export async function createCampaign(
  tenantId: string,
  body: OutboundCampaignCreate,
): Promise<OutboundCampaign> {
  return apiFetch<OutboundCampaign>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/campaigns`,
    { method: 'POST', body },
  );
}

export async function updateCampaign(
  tenantId: string,
  campaignId: string,
  patch: OutboundCampaignPatch,
  ifMatchVersion: number,
): Promise<OutboundCampaign> {
  return apiFetch<OutboundCampaign>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/campaigns/${encodeURIComponent(campaignId)}`,
    {
      method: 'PATCH',
      body: patch,
      headers: { 'If-Match': String(ifMatchVersion) },
    },
  );
}

export async function archiveCampaign(
  tenantId: string,
  campaignId: string,
): Promise<OutboundCampaign> {
  return apiFetch<OutboundCampaign>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/campaigns/${encodeURIComponent(campaignId)}/archive`,
    { method: 'POST' },
  );
}

export async function issueUploadUrl(
  tenantId: string,
  campaignId: string,
  filename?: string,
): Promise<UploadUrlResponse> {
  return apiFetch<UploadUrlResponse>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/campaigns/${encodeURIComponent(campaignId)}/contacts/upload-url`,
    { method: 'POST', body: filename ? { filename } : {} },
  );
}

export async function executeCampaign(
  tenantId: string,
  campaignId: string,
  scheduledAt?: string,
): Promise<OutboundCampaign> {
  return apiFetch<OutboundCampaign>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/campaigns/${encodeURIComponent(campaignId)}/execute`,
    { method: 'POST', body: scheduledAt ? { scheduledAt } : {} },
  );
}
